import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './CourtList.css';
import { useUser } from '../context/UserContext';
import { hasPrivilege } from '../utils/hasPrivilege';
import axios from 'axios';
import Swal from "sweetalert2";
import { Bar } from 'react-chartjs-2';
import { FiChevronDown, FiRefreshCw } from 'react-icons/fi';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const CourtTimingsChart = ({ courts }) => {
  const labels = courts.map(court => court.name);
  const timings = courts.map(court => {
    const timingStrings = court.timing.split(', ');
    const totalMinutes = timingStrings.reduce((acc, timing) => {
      const [start, end] = timing.split(' - ');
      const startTime = new Date(`1970-01-01T${start}`);
      const endTime = new Date(`1970-01-01T${end}`);
      const duration = (endTime - startTime) / (1000 * 60);
      return acc + duration;
    }, 0);
    return totalMinutes;
  });

  const timingDetails = courts.map(court => court.timing);

  const data = {
    labels: labels,
    datasets: [
      {
        label: 'Total Court Time (minutes)',
        data: timings,
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        borderColor: 'rgba(75, 192, 192, 1)',
        borderWidth: 1,
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Court Timings',
      },
      tooltip: {
        callbacks: {
          afterBody: (context) => {
            const index = context[0].dataIndex;
            return `Timings: ${timingDetails[index]}`;
          },
        },
      },
    },
  };

  return <Bar data={data} options={options} />;
};

const CourtList = () => {
  const { userData } = useUser();
  const { selectedCenter, setSelectedCenter } = useUser();
  const token = localStorage.getItem('auth-token');
  const [searchTerm, setSearchTerm] = useState('');
  const [courts, setCourts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchCourts = async () => {
    setLoading(true)
    setError('')
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_COURT_INVENTORY}?academyId=${selectedCenter}`,
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (response.data.success && response.data.data?.courts) {
        const { courts: fetchedCourts } = response.data.data;

        const formattedCourts = fetchedCourts.map((court) => ({
          id: court?.id,
          name: court?.name || '-',
          sport: court?.sportName || '-',
          timing: court?.courtTimings?.map(timing => `${timing.openTime} - ${timing.closeTime}`).join(', ') || '-',
        }));
        formattedCourts.sort((a, b) => a.id - b.id);
        setCourts(formattedCourts);
      } else {
      }
    } catch (err) {
      setError('Error fetching data: ' + error.message);

    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (selectedCenter) {
      fetchCourts();
    }
  }, [selectedCenter]);

  const headers = [
    { label: 'Name', key: 'name' },
    { label: 'Sport', key: 'sport' },
    { label: 'Timing', key: 'timing' },
  ];
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setrowsPerPage] = useState(10);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  const handleCreateCourt = (event) => {
    if (!selectedCenter) return;

    const url = `/courtCreation?academyId=${selectedCenter}`;
    if (event.metaKey || event.ctrlKey) {
      window.open(url, "_blank");
    } else {
      navigate(url);
    }
  };


  const filteredCourts = courts.filter((court) =>
    Object.values(court)
      .join(' ')
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: 'ascending'
  });


  const handleSort = (columnKey) => {
    let direction = 'ascending';
    if (sortConfig.key === columnKey && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key: columnKey, direction });
  };

  const sortData = (data) => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return sortConfig.direction === 'ascending' ? 1 : -1;
      if (bValue == null) return sortConfig.direction === 'ascending' ? -1 : 1;

      if (sortConfig.key === 'name' || sortConfig.key === 'sport' || sortConfig.key === 'timing') {
        const aStr = String(aValue).toLowerCase();
        const bStr = String(bValue).toLowerCase();
        const comparison = aStr.localeCompare(bStr);
        return sortConfig.direction === 'ascending' ? comparison : -comparison;
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'ascending' ? aValue - bValue : bValue - aValue;
      }

      if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
      return 0;
    });
  };

  const sortedData = sortData(filteredCourts);
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = sortedData.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(sortedData.length / rowsPerPage);

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage((prevPage) => prevPage + 1);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage((prevPage) => prevPage - 1);
  };

  const generatePageNumbers = () => {
    const pages = [];
    const totalPagesToShow = 5;

    if (totalPages <= totalPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });

  const toggleDropdown = (courtId, e) => {
    e.stopPropagation();
    if (dropdownOpen === courtId) {
      return setDropdownOpen(null);
    }
    const { bottom, left } = e.currentTarget.getBoundingClientRect();
    setDropdownPos({
      top: bottom + window.scrollY,
      left: left + window.scrollX,
    });
    setDropdownOpen(courtId);
  };
  useEffect(() => {
    const closeOnScroll = () => setDropdownOpen(null);

    window.addEventListener('scroll', closeOnScroll, true);
    return () => window.removeEventListener('scroll', closeOnScroll, true);
  }, []);
  const renderPagination = () => {
    return generatePageNumbers().map((page, index) =>
      typeof page === 'number' ? (
        <button
          key={index}
          className={`page-number ${currentPage === page ? 'active' : ''}`}
          onClick={() => handlePageClick(page)}
        >
          {page}
        </button>
      ) : (
        <span key={index} className="pagination-dots">
          {page}
        </span>
      )
    );
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const dropdown = document.querySelector('.custom-dropdown-player');
      if (dropdown && !dropdown.contains(event.target) && !event.target.closest('.dots-btn-player')) {
        setDropdownOpen(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  const handleItemsPerPageChange = (event) => {
    setrowsPerPage(Number(event.target.value));
    setCurrentPage(1);
  };

  const handleEdit = (event, courtId) => {
    const url = `/courtCreation?courtId=${courtId}&academyId=${selectedCenter}`;
    if (event.metaKey || event.ctrlKey) {
      window.open(url, "_blank");
    } else {
      navigate(url);
    }
  };


  const handleDelete = async (courtId) => {
    if (!selectedCenter) {
      Swal.fire({
        icon: "warning",
        title: "No academy selected",
        text: "Please select an academy before deleting a court.",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });

      return;
    }

    const confirmDelete = await Swal.fire({
      icon: "warning",
      title: "Are you sure?",
      text: "Do you really want to delete this court?",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel",
    });
    if (!confirmDelete.isConfirmed) return;

    try {
      const response = await axios.get(`${process.env.REACT_APP_API_COURT}/${courtId}/delete`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        }
      });

      if (response.data.success) {
        Swal.fire({
          icon: "success",
          title: "Deleted!",
          text: "Court deleted successfully.",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
        });

        setCourts((prevCourts) => prevCourts.filter((court) => court.id !== courtId));
      } else {
        Swal.fire({
          icon: "error",
          title: "Failed",
          text: "Failed to delete court.",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || error.message,
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });

    }
  };

  const handleUnavailableDate = (event, courtId) => {
    const url = `/unavailableDate?courtId=${courtId}&academyId=${selectedCenter}`;
    if (event.metaKey || event.ctrlKey) {
      window.open(url, "_blank");
    } else {
      navigate(url);
    }
  };

  const totalEntries = filteredCourts.length;
  const indexOfFirstEntry = totalEntries > 0 ? (currentPage - 1) * rowsPerPage + 1 : 0;
  const indexOfLastEntry = totalEntries > 0 ? Math.min(currentPage * rowsPerPage, totalEntries) : 0;
  return (
    <div className="enrollment-container">
      {/* {!loading && !error && courts.length > 0 && (
        <div className="court-timings-container">
          <CourtTimingsChart courts={courts} />
        </div>
      )} */}

      <div className="search-bar">
        <div className="input-container">
          <input
            type="text"
            id="searchInput"
            placeholder=""
            className={`search-input ${searchTerm ? "has-value" : ""}`}
            value={searchTerm}
            onChange={handleSearchChange}
          />
          <label htmlFor="searchInput" className="floating-label">
            🔍 Search
          </label>
        </div>
        <button
          className="create-button-new"
          onClick={handleCreateCourt}
        >
          <span className="create-button__icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              width="23"
              height="23"
              className="create-button__svg"
            >
              <path
                d="M12 5v14M5 12h14"
                stroke="white"
                strokeWidth="2.3"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <span className="create-button__label">
            Create Court
          </span>
        </button>
      </div>
      <div className="items-and-dues">
        <select className="items-per-page-select" value={rowsPerPage} onChange={handleItemsPerPageChange}>
          <option value={10}>10 items per page</option>
          <option value={50}>50 items per page</option>
          <option value={75}>75 items per page</option>
          <option value={100}>100 items per page</option>
        </select>
        <div className="pagination-selector">
          <div className="button-group">
            <div className="group-label">Items per page:</div>
            <div className="button-options">
              {[10, 50, 75, 100].map((option) => (
                <button
                  key={option}
                  onClick={() =>
                    handleItemsPerPageChange({ target: { value: option } })
                  }
                  className={`page-button ${rowsPerPage === option ? 'active' : ''}`}
                >
                  <span className="button-text">{option}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
        <button className="tt-action-btn" onClick={fetchCourts}>
          <FiRefreshCw className={`refresh-icon ${loading ? 'spin' : ''}`} />
          Refresh
        </button>

      </div>

      {loading && (
        <div className="premium-loader">
          <div className="progress-track">
            <div className="progress-bar"></div>
          </div>
          <div className="status-text">
            <span>L</span>
            <span>O</span>
            <span>A</span>
            <span>D</span>
            <span>I</span>
            <span>N</span>
            <span>G</span>
          </div>
        </div>
      )}
      {error && <div className="error-message">{error}</div>}
      {!loading && !error && filteredCourts.length === 0 && (
        <div className="error-message">No data available</div>
      )}

      <div className="payment-list-scroll-container" style={{ overflowY: 'auto' }}>
        <table className="payment-list-table">
          <thead>
            <tr>
              {headers.map(({ key, label }) => (
                <th key={key} className="challenges-column" onClick={() => handleSort(key)}>
                  <div className="th-content">
                    <span>{label}</span>
                    <FiChevronDown
                      className={`sort-icon ${sortConfig.key === key ? 'active' : ''
                        } ${sortConfig.key === key && sortConfig.direction === 'descending' ? 'desc' : ''}`}
                    />
                  </div>
                </th>
              ))}
              {hasPrivilege(userData, 'COURTS', 'COURT_LIST_MODIFY') && (
                <th>Action</th>
              )}
            </tr>
          </thead>

          <tbody>
            {currentRows.map((court) => (
              <tr key={court.id} className="courtlist-row">
                <td className="courtlist-cell courtlist-cell-name">{court.name}</td>
                <td className="courtlist-cell courtlist-cell-sport">{court.sport}</td>
                <td className="courtlist-cell courtlist-cell-timing" style={{ minWidth: '500px' }}>{court.timing}</td>
                {hasPrivilege(userData, 'COURTS', 'COURT_LIST_MODIFY') && (
                  <td className="sport-actions-column">
                    <button
                      className="dots-btn-player"
                      type="button"
                      onClick={(e) => toggleDropdown(court.id, e)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '24px',
                      }}
                    >
                      &#x22EE;
                    </button>

                    {dropdownOpen === court.id && (
                      <div
                        className="custom-dropdown-player"
                        style={{
                          position: 'absolute',
                          top: dropdownPos.top,
                          left: dropdownPos.left,
                          zIndex: 9999,
                          backgroundColor: 'white',
                          border: '1px solid #ccc',
                          borderRadius: '4px',
                          boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                          minWidth: '150px',
                        }}
                      >
                        <button
                          type="button"
                          className="dropdown-item"
                          onClick={(e) => handleEdit(e, court.id)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="dropdown-item"
                          onClick={() => handleDelete(court.id)}
                        >
                          Delete
                        </button>
                        <button
                          type="button"
                          className="dropdown-item"
                          onClick={(e) => handleUnavailableDate(e, court.id)}
                        >
                          Unavailable Dates
                        </button>


                      </div>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="pagination">
        <div className="showing-entries">
          Showing {indexOfFirstEntry} to {indexOfLastEntry} of {totalEntries} entries
        </div>
        <button
          className="prev-button"
          onClick={handlePrevPage}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        {renderPagination()}
        <button
          className="next-button"
          onClick={handleNextPage}
          disabled={currentPage === totalPages}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default CourtList;
